'use client'

import NavLink from '@/components/ui/NavLink'
import StackIcon from '@/components/content/stacks/shared/StackIcon'
import { Placeholder } from '@/components/ui/Placeholder'
import { MenuTitle } from '@/components/navigation/SubMenu'
import { Icon } from '@/icons/Icon'
import { useStacksMenuContext } from '@/context/NavbarContext'
import { createLogger } from '@/lib/logger'
import type { StackAppsApp, Stack, Category, App } from '@/payload-types'

const logger = createLogger('StacksMenu')

type StackLinkProps = {
  stack: Stack
}

export default function StacksMenu() {
  const { stacks, categories, categoryCounts = {} } = useStacksMenuContext()

  return (
    <div
      data-menu="Stacks"
      className="flex w-full shrink-0 flex-col divide-x divide-gray-200 dark:divide-white/10 sm:flex-row navigation:w-[680px]"
    >
      <div className="flex w-full flex-col gap-2 px-2 py-5">
        <MenuTitle title="Selected Stacks" />
        <div className="grid w-full grid-cols-1 md:grid-cols-2 navigation:gap-5">
          {stacks.length > 0
            ? stacks.map((stack, index) => {
                if (stack.stackApps) return <StackLink key={index} stack={stack} />
              })
            : Array.from({ length: 3 }, (_, index) => <Placeholder className="h-20" key={index} />)}
        </div>
      </div>

      {/* <div className="flex w-full flex-col gap-2 px-2 pb-2 pt-2 sm:w-1/2 sm:pt-5">
        <MenuTitle title="By Category" />
        {categories.length > 0
          ? categories.map((category, index) => <CategoryLink key={index} category={category} />)
          : Array.from({ length: 7 }, (_, index) => <Placeholder className="h-10" key={index} />)}

        <NavLink
          href="/stacks#all"
          className="group flex h-14 items-center gap-2 px-5 text-13 text-gray-500 transition-colors hover:text-gray-950 dark:hover:text-white"
        >
          All stacks by category
          <div className="rotate-180 transition-colors">
            <Icon name="chevron-left" size={12} />
          </div>
        </NavLink>
      </div> */}
    </div>
  )
}

export function StackLink({ stack }: StackLinkProps) {
  const stackSlug = stack?.slug

  if (!stackSlug) {
    logger.error('Stack Slug is missing in StackLink')
    return null
  }

  const link = `/stacks/${stackSlug}`
  const stackApps = stack.stackApps?.docs as StackAppsApp[]
  const apps = stackApps
    ?.map((item) => {
      if (typeof item.app === 'object') {
        return item.app as App
      }
      return undefined
    })
    .filter((app): app is App => app !== undefined)

  return (
    <NavLink
      href={link}
      className="group flex items-center gap-5 rounded-xl px-4 py-3 transition-colors duration-300 hover:bg-gray-100 dark:hover:bg-white/10"
    >
      <StackIcon apps={apps} />
      <div className="flex w-full flex-col gap-2 pt-0.5">
        <div className="flex items-center gap-2">
          <span className="truncate whitespace-nowrap text-14 font-medium leading-none text-gray-950 transition-colors duration-300 group-hover:text-primary-500 dark:text-white">
            {stack.name}
          </span>
        </div>

        <div className="w-full text-12 text-zinc-500">{stack.description}</div>
      </div>
    </NavLink>
  )
}

export function CategoryLink({ category }: { category: Category }) {
  if (category) {
    return (
      <NavLink
        href={`/stacks#${category.slug}`}
        className="group flex h-10 items-center gap-5 rounded-xl px-5 transition-colors duration-300 hover:bg-gray-100 dark:hover:bg-white/10"
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-[0.75px] border-black/10 bg-white text-primary-500 dark:border-white/10 dark:bg-black dark:text-white">
          {category.icon && <Icon name={category.icon} size={12} strokeWidth={1} />}
        </div>
        <span className="w-full text-13 font-medium text-gray-950 transition-colors duration-300 dark:text-white">
          {category.name}
        </span>
      </NavLink>
    )
  }
}
